import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import api from '../services/api';

// Clave para el almacenamiento local
const USER_STORAGE_KEY = 'playmitos_user_data';

export const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [error, setError] = useState(null);

  const checkSession = useCallback(async () => {
    try {
      console.log('🔍 Verificando sesión...');
      setIsAuthenticating(true);
      setError(null);
      
      const response = await api.get('/api/auth/me', {
        withCredentials: true,
        validateStatus: function (status) {
          return (status >= 200 && status < 300) || status === 401;
        }
      });

      console.log('📡 Respuesta de /api/auth/me:', {
        status: response.status,
        data: response.data
      });

      if (response.status === 200 && response.data.authenticated) {
        console.log('✅ Sesión verificada correctamente');
        console.log('👤 Usuario:', response.data.user);
        setUser(response.data.user);
        return true;
      }

      console.log('❌ Sesión no válida, limpiando datos');
      setUser(null);
      return false;
    } catch (error) {
      console.error('🚨 Error al verificar sesión:', error);
      setUser(null);
      return false;
    } finally {
      setIsAuthenticating(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      console.log('🔑 Intentando login...');
      setIsAuthenticating(true);
      setError(null);
      
      const response = await api.post('/api/auth/login', { 
        email, 
        password 
      }, {
        withCredentials: true
      });
      
      console.log('📡 Respuesta de login:', response.data);
      
      if (response.data.success) {
        console.log('✅ Login exitoso');
        console.log('👤 Usuario:', response.data.user);
        setUser(response.data.user);
        return true;
      }
      
      console.log('❌ Login fallido');
      setError('Email o contraseña incorrectos');
      return false;
    } catch (error) {
      console.error('🚨 Error en login:', error);
      if (error.code === 'ERR_NETWORK') {
        setError('Error de conexión con el servidor. Por favor, intente más tarde.');
      } else {
        setError(error.response?.data?.error || 'Error al iniciar sesión');
      }
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setIsAuthenticating(true);
      await api.post('/api/auth/logout', {}, {
        withCredentials: true
      });
      console.log('Logout exitoso');
      setUser(null);
    } catch (error) {
      console.error('Error en logout:', error);
      setUser(null);
    } finally {
      setIsAuthenticating(false);
    }
  }, []);

  // Verificar sesión al montar el componente
  useEffect(() => {
    console.log('🔄 AuthProvider montado');
    let isMounted = true;

    const initializeAuth = async () => {
      if (isMounted) {
        console.log('🚀 Inicializando autenticación...');
        await checkSession();
      }
    };
    initializeAuth();

    return () => {
      console.log('👋 AuthProvider desmontado');
      isMounted = false;
    };
  }, [checkSession]);

  // Verificar sesión periódicamente (cada 5 minutos)
  useEffect(() => {
    // Only set up/clear the interval based on user state.
    // The initial check on mount and the interval itself handle verification.
    if (!user) {
      // If user becomes null (logout), the cleanup function (return) will clear any existing interval.
      return;
    }

    // If user exists, set up the interval.
    console.log('⏰ Configurando verificación periódica (intervalo). Usuario actual:', user);
    let isMounted = true;

    const interval = setInterval(async () => {
      if (isMounted) {
        console.log('🔄 Verificando sesión periódicamente (intervalo)...');
        await checkSession();
      }
    }, 5 * 60 * 1000); // 5 minutos

    // Cleanup function: clears the interval when the component unmounts
    // or when the 'user' state becomes null (causing the effect to re-run and hit the 'if (!user) return;' early).
    return () => {
      console.log('🛑 Limpiando intervalo de verificación');
      clearInterval(interval);
      isMounted = false;
    };
    // Keep dependencies: this effect needs to re-run when user changes to correctly manage the interval lifecycle.
  }, [user, checkSession]);

  const contextValue = {
    user,
    login,
    logout,
    checkSession,
    isAuthenticating,
    error
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}